import {
  dollarsToCents,
  dollarsToHundredthsOfCents,
  pricePerUnitToDollars,
  toTaxPercent,
} from "src/main/Billing/Items/helpers"

export const createAvailabilityDefaults = (options) =>
  options.reduce((acc, type) => {
    const { value, checked } = type
    acc[value] = checked
    return acc
  }, {})

export const validatePrecision = (precision) => (number) => {
  const [, decimals] = String(number).split(".")

  if (decimals && decimals.length > precision) {
    if (precision === 0) {
      return `Please enter a whole number.`
    } else {
      return `Specify up to ${precision} decimal places.`
    }
  }
}

export const pricePrecisionForType = (type) => {
  switch (type) {
    case "fuel":
      return 4
    default:
      return 2
  }
}

export const defaultRecurrenceValue = (defaultRecurrence) =>
  defaultRecurrence === "does_not_repeat" ? null : defaultRecurrence

export const availabilitiesValue = (availabilities) =>
  Object.keys(availabilities).filter(
    (availability) => availabilities[availability]
  )

export const dollarsToPrecisionForType = ({ type, dollars }) => {
  if (!dollars) return null

  switch (type) {
    case "fuel":
      return dollarsToHundredthsOfCents(dollars)
    default:
      return dollarsToCents(dollars)
  }
}

// The combined rate an item currently has applied on the itemized tax path:
// marina taxes it has not been excluded from, plus its own product taxes.
export const appliedTaxRate = ({
  marinaTaxes = [],
  appliedTaxIds = [],
  productTaxesAttributes = [],
}) => {
  const marinaTaxRate = marinaTaxes
    .filter((tax) => appliedTaxIds.includes(tax.id))
    .reduce((sum, tax) => sum + tax.rate, 0)
  const productTaxRate = productTaxesAttributes
    .filter((tax) => !tax._destroy)
    .reduce((sum, tax) => sum + tax.rate, 0)

  return marinaTaxRate + productTaxRate
}

// Every item type carries the control, so this applies uniformly. A new item starts
// taxable and the nudge asks about it, rather than the form guessing.
export const withTaxableDefault = ({ defaultValues, product }) => {
  if (!defaultValues) return defaultValues

  return {
    ...defaultValues,
    taxable: product ? Boolean(product.taxable) : true,
  }
}

export const activeChildItemsForProduct = (product) => {
  return product?.inventoryChildProducts?.filter(
    (childProduct) =>
      childProduct.deletedAt === null || childProduct.deleted_at === null
  )
}

export const getAvailabilitiesForType = ({
  type,
  availabilityOptions,
  electricType,
  isListedPumpPrice,
  isCancellationFee = false,
}) => {
  switch (type) {
    case "electric":
      return availabilityOptions.electric[electricType]
    case "fuel":
      return isListedPumpPrice
        ? availabilityOptions.fuel.listed_pump_price
        : availabilityOptions.fuel.default
    case "fee":
      return availabilityOptions.fee.map((option) =>
        option.value === "cancellation_fees"
          ? { ...option, checked: isCancellationFee }
          : option
      )
    default:
      return availabilityOptions[type]
  }
}

const CANCELLATION_FEE_PRODUCT_CATEGORY_NAME = "Other"

export const defaultValuesForType = ({
  type,
  availabilityOptions,
  electricType = "non_metered",
  productCategories,
  marinaTaxes = [],
}) => {
  const urlParams = new URLSearchParams(window.location.search)
  const isOverrideForCancellationFee =
    type === "fee" && urlParams.get("type") === "cancellationFee"
  const cancellationFeeCategory = productCategories?.find(
    (category) =>
      category.displayName === CANCELLATION_FEE_PRODUCT_CATEGORY_NAME
  )
  const availabilitiesForType = getAvailabilitiesForType({
    type,
    availabilityOptions,
    electricType,
    isListedPumpPrice: false,
    isCancellationFee: isOverrideForCancellationFee,
  })

  const availabilities = createAvailabilityDefaults(availabilitiesForType)
  const allTaxIds = marinaTaxes.map((tax) => tax.id)

  switch (type) {
    case "standard":
      return {
        // intake
        type: "standard",
        name: null,
        sku: null,
        barcode: null,
        // price
        defaultPriceType: "per_quantity",
        defaultPrice: null,
        defaultTax: null,
        // category
        category: null,
        // inventory
        isInventory: false,
        lowQuantityBalanceThreshold: null,
        // details
        defaultRecurrence: "does_not_repeat",
        // availability
        availabilities,
        // taxes
        appliedTaxIds: allTaxIds,
        productTaxesAttributes: [],
      }
    case "boat_storage":
      return {
        // intake
        type: "boat_storage",
        name: null,
        // category
        category: null,
        // details
        spaceType: "Dock",
        // availability
        availabilities,
        // taxes
        appliedTaxIds: allTaxIds,
        productTaxesAttributes: [],
      }
    case "deposit":
      return {
        // intake
        type: "deposit",
        name: null,
        // price
        defaultPrice: null,
        defaultTax: null,
        // category
        category: null,
        // availability
        availabilities,
        // taxes
        appliedTaxIds: allTaxIds,
        productTaxesAttributes: [],
      }
    case "fee":
      return {
        // intake
        type: "fee",
        name: null,
        // price
        defaultPriceType: "per_quantity",
        defaultPrice: null,
        defaultTax: null,
        // category
        category: isOverrideForCancellationFee
          ? cancellationFeeCategory?.value
          : null,
        // availability
        availabilities,
        // taxes
        appliedTaxIds: allTaxIds,
        productTaxesAttributes: [],
      }
    case "fuel":
      return {
        // intake
        type: "fuel",
        name: null,
        sku: null,
        barcode: null,
        // price
        defaultPrice: null,
        defaultTax: null,
        // category
        category: null,
        // inventory
        isInventory: false,
        lowQuantityBalanceThreshold: null,
        inventoryProductId: null,
        // availability
        availabilities,
        // taxes
        appliedTaxIds: allTaxIds,
        productTaxesAttributes: [],
      }
    case "electric":
      if (electricType === "non_metered") {
        return {
          // intake
          type: "electric",
          name: null,
          electricType: "non_metered",
          // price
          defaultPriceType: "per_day",
          defaultPrice: null,
          defaultTax: null,
          // category
          category: null,
          // details
          defaultRecurrence: "does_not_repeat",
          // availability
          availabilities,
          // taxes
          appliedTaxIds: allTaxIds,
          productTaxesAttributes: [],
        }
      } else {
        return {
          // intake
          type: "electric",
          name: null,
          electricType: "metered",
          // price
          defaultPrice: null,
          defaultTax: null,
          // category
          category: null,
          // availability
          availabilities,
          // taxes
          appliedTaxIds: allTaxIds,
          productTaxesAttributes: [],
        }
      }
    case "labor":
      return {
        // intake
        type: "labor",
        name: null,
        // price
        defaultPriceType: "per_quantity",
        defaultPrice: null,
        defaultTax: null,
        // category
        category: null,
        // availability
        availabilities,
        // taxes
        appliedTaxIds: allTaxIds,
        productTaxesAttributes: [],
      }
  }
}

export const defaultValuesForProduct = ({
  product,
  availabilityOptions,
  marinaTaxes = [],
}) => {
  const availabilitiesForType = getAvailabilitiesForType({
    type: product.productType,
    availabilityOptions,
    electricType: product.electricType,
    isListedPumpPrice: product.isListedPumpPrice,
  })

  const productAvailabilities = product.availabilities.map((a) => a.key)
  const selectedAvailabilities = availabilitiesForType.map((availability) => {
    const checked = productAvailabilities.includes(availability.value)
    return { ...availability, checked }
  })
  const availabilities = createAvailabilityDefaults(selectedAvailabilities)

  // Calculate applied taxes: all taxes minus exclusions
  const excludedTaxIds = product.excludedMarinaTaxIds || []
  const appliedTaxIds = marinaTaxes
    .map((tax) => tax.id)
    .filter((id) => !excludedTaxIds.includes(id))

  const productTaxesAttributes = product.productTaxes || []

  switch (product.productType) {
    case "standard":
      return {
        // intake
        type: "standard",
        name: product.name,
        sku: product.sku,
        barcode: product.barcode,
        // price
        defaultPriceType: product.defaultPricingStructure,
        defaultPrice: product.pricePerUnit
          ? pricePerUnitToDollars({
              pricePerUnit: product.pricePerUnit,
              precision: product.pricePrecision,
            })
          : null,
        defaultTax: product.taxRate ? toTaxPercent(product.taxRate) : null,
        // category
        category: product.productCategory?.id,
        // inventory
        isInventory: product.isInventory,
        lowQuantityBalanceThreshold: product.lowQuantityBalanceThreshold,
        // details
        defaultRecurrence: product.defaultRecurrence ?? "does_not_repeat",
        // availability
        availabilities,
        // taxes
        appliedTaxIds,
        productTaxesAttributes,
      }
    case "boat_storage":
      return {
        // intake
        type: "boat_storage",
        name: product.name,
        // category
        category: product.productCategory?.id,
        // details
        spaceType: product.spaceType,
        // availability
        availabilities,
        // taxes
        appliedTaxIds,
        productTaxesAttributes,
      }
    case "deposit":
      return {
        // intake
        type: "deposit",
        name: product.name,
        // price
        defaultPrice: product.pricePerUnit
          ? pricePerUnitToDollars({
              pricePerUnit: product.pricePerUnit,
              precision: product.pricePrecision,
            })
          : null,
        defaultTax: product.taxRate ? toTaxPercent(product.taxRate) : null,
        // category
        category: product.productCategory?.id,
        // availability
        availabilities,
        // taxes
        appliedTaxIds,
        productTaxesAttributes,
      }
    case "fee":
      return {
        // intake
        type: "fee",
        name: product.name,
        // price
        defaultPriceType: product.defaultPricingStructure,
        defaultPrice: product.pricePerUnit
          ? pricePerUnitToDollars({
              pricePerUnit: product.pricePerUnit,
              precision: product.pricePrecision,
            })
          : null,
        defaultTax: product.taxRate ? toTaxPercent(product.taxRate) : null,
        // category
        category: product.productCategory?.id,
        // availability
        availabilities,
        // taxes
        appliedTaxIds,
        productTaxesAttributes,
      }
    case "fuel":
      return {
        // intake
        type: "fuel",
        name: product.name,
        sku: product.sku,
        barcode: product.barcode,
        // price
        defaultPrice: product.pricePerUnit
          ? pricePerUnitToDollars({
              pricePerUnit: product.pricePerUnit,
              precision: product.pricePrecision,
            })
          : null,
        defaultTax: product.taxRate ? toTaxPercent(product.taxRate) : null,
        // category
        category: product.productCategory?.id,
        // inventory
        isInventory: product.isInventory,
        lowQuantityBalanceThreshold: product.lowQuantityBalanceThreshold,
        inventoryProductId: product.inventoryProductId,
        // availability
        availabilities,
        // taxes
        appliedTaxIds,
        productTaxesAttributes,
      }
    case "electric":
      if (product.electricType === "non_metered") {
        return {
          // intake
          type: "electric",
          name: product.name,
          electricType: "non_metered",
          // price
          defaultPriceType: product.defaultPricingStructure,
          defaultPrice: product.pricePerUnit
            ? pricePerUnitToDollars({
                pricePerUnit: product.pricePerUnit,
                precision: product.pricePrecision,
              })
            : null,
          defaultTax: product.taxRate ? toTaxPercent(product.taxRate) : null,
          // category
          category: product.productCategory?.id,
          // details
          defaultRecurrence: product.defaultRecurrence ?? "does_not_repeat",
          // availability
          availabilities,
          // taxes
          appliedTaxIds,
          productTaxesAttributes,
        }
      } else {
        return {
          // intake
          type: "electric",
          name: product.name,
          electricType: "metered",
          // price
          defaultPrice: product.pricePerUnit
            ? pricePerUnitToDollars({
                pricePerUnit: product.pricePerUnit,
                precision: product.pricePrecision,
              })
            : null,
          defaultTax: product.taxRate ? toTaxPercent(product.taxRate) : null,
          // category
          category: product.productCategory?.id,
          // availability
          availabilities,
          // taxes
          appliedTaxIds,
          productTaxesAttributes,
        }
      }
    case "labor":
      return {
        // intake
        type: "labor",
        name: product.name,
        // price
        defaultPriceType: product.defaultPricingStructure,
        defaultPrice: product.pricePerUnit
          ? pricePerUnitToDollars({
              pricePerUnit: product.pricePerUnit,
              precision: product.pricePrecision,
            })
          : null,
        defaultTax: product.taxRate ? toTaxPercent(product.taxRate) : null,
        // category
        category: product.productCategory?.id,
        // availability
        availabilities,
        // taxes
        appliedTaxIds,
        productTaxesAttributes,
      }
  }
}
